import type { Components, Theme } from '@mui/material';

export const components: Components<Theme> = {
  MuiButton: {
    defaultProps: {
      disableElevation: true,
      variant: 'contained',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        fontWeight: 600,
        padding: '8px 20px',
        transition: 'all 0.2s ease-in-out',
        // contained (default) — brand lime bg with black text
        '&.MuiButton-contained': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            color: '#FFFFFF',
          },
          '&:disabled': {
            backgroundColor: theme.palette.divider,
            color: theme.palette.text.disabled,
          },
        },
        // outlined — branded border, no fill
        '&.MuiButton-outlined': {
          borderColor: theme.palette.primary.dark,
          color: theme.palette.primary.dark,
          '&:hover': {
            backgroundColor: theme.palette.primary.light,
            borderColor: theme.palette.primary.dark,
          },
        },
      }),
      sizeSmall: {
        padding: '6px 14px',
        fontSize: '0.8125rem',
      },
      sizeLarge: {
        padding: '12px 28px',
        fontSize: '1rem',
      },
    },
  },

  MuiSvgIcon: {
    styleOverrides: {
      root: ({ theme }) => ({
        // Default: primary.dark (Figma). With admin branding: primary.main (soft brand color).
        // Descriptive icon labels use text.secondary (#525866) — applied per-component.
        color: theme.palette.surface.iconColor,
      }),
    },
  },

  MuiTextField: {
    defaultProps: {
      variant: 'outlined',
      size: 'medium',
      fullWidth: true,
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.dark,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.dark,
        },
      }),
      input: ({ theme }) => ({
        '&::placeholder': {
          color: theme.palette.text.placeholder,
          opacity: 1,
        },
      }),
    },
  },

  MuiInputBase: {
    styleOverrides: {
      input: ({ theme }) => ({
        '&::placeholder': {
          color: theme.palette.text.placeholder,
          opacity: 1,
        },
      }),
    },
  },

  MuiRadio: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.divider,
        '&.Mui-checked': {
          color: theme.palette.primary.dark,
        },
      }),
    },
  },

  MuiCard: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius * 1.5,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
      }),
    },
  },

  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: 24,
        '&:last-child': {
          paddingBottom: 24,
        },
      },
    },
  },

  MuiAppBar: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      // Reset to transparent so individual pages can apply their own nav style
      root: ({ theme }) => ({
        backgroundColor: 'transparent',
        boxShadow: 'none',
        color: theme.palette.text.primary,
      }),
    },
  },

  MuiDialog: {
    defaultProps: {
      PaperProps: {
        elevation: 0,
      },
    },
    styleOverrides: {
      paper: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius * 1.5,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.15)',
      }),
    },
  },

  MuiChip: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        fontWeight: 500,
      }),
    },
  },

  MuiTooltip: {
    defaultProps: {
      arrow: true,
    },
    styleOverrides: {
      tooltip: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius / 2,
        fontSize: '0.75rem',
        fontWeight: 500,
        backgroundColor: theme.palette.mode === 'light' ? '#00332D' : '#E6F4C0',
        color: theme.palette.mode === 'light' ? '#FFFFFF' : '#00332D',
      }),
    },
  },

  MuiLinearProgress: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        height: 6,
        backgroundColor: theme.palette.primary.light,
      }),
      bar: ({ theme }) => ({
        backgroundColor: theme.palette.primary.dark,
      }),
    },
  },

  MuiAlert: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
      }),
    },
  },

  MuiSkeleton: {
    defaultProps: {
      animation: 'wave',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
      }),
    },
  },
};
